// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@mantine/core';

import styles from './ListCustomers.module.scss';
import useExternalApiService from 'helpers/ExternalApiService';
import { store } from 'stores/Store';
import { ReactComponent as UserIcon } from "assets/user2.svg";
import { ReactComponent as LeadIcon } from "assets/UserPlus.svg";
import { ReactComponent as MagnifyingGlassIcon } from 'assets/magnifyingGlass.svg';
import { ReactComponent as FunnelIcon } from 'assets/funnel.svg';
import { ReactComponent as BlueFunnelIcon } from 'assets/blueFunnel.svg';
import { ReactComponent as AlarmIcon } from 'assets/alarm.svg';
import { ReactComponent as BlueAlarmIcon } from 'assets/blueAlarm.svg';
import { ReactComponent as ClockCounterClockwiseIcon } from 'assets/clockCounterClockwise.svg';
import Funnel from './components/Funnel';  // V1 Funnel (old)
// TODO: Re-enable once V2 funnel issues are fixed
// import SalesFunnel from 'Modules/Funnel';  // V2 SalesFunnel (new industry-standard)
import Pipeline from './components/Pipeline';
import FilterCarousel from './components/FilterCarousel';
import FilterDropdown from './components/FilterDropdown';
import { ReactComponent as ExpandIcon } from 'assets/expand.svg';
import { Tab as PractitionerBaseballCardTab } from 'Modules/PractitionerBaseballCard';

import { ReactComponent as CheckIcon } from "assets/verified.svg";
import { ReactComponent as FadersIcon } from "assets/faders.svg";
import { ReactComponent as CurrencyCircleDollarIcon } from "assets/CurrencyCircleDollar.svg";
import { ReactComponent as NotesIcon } from "assets/notes.svg";
import FollowUpsTab from './FollowUpsTab';
import Tab from './ListCustomersTab';
import { Tab as BaseballCardTab } from '../PractitionerBaseballCard';
import Toastr from '../../components/Toastr';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useLatestPractitionersUpdated } from 'hooks/useLatestPractitionersUpdated';
import LatestPractitionersUpdatedList from 'components/LatestPractitionersUpdatedList';

interface Props {
  defaultTab?: Tab;
  onPractitionerSelect: (initialTab?: BaseballCardTab) => void;
  onOpenLeadCreate: () => void;
  //FIXME: this is not the best solution for forcing cache clear
  // maybe adding store.cache as one of the deps of the fetch
  // useEffect would be a better idea
  // TLDR: this is not the react way of doing this.
  getFetchPractsAction?: (fetchPractsFunction: () => void) => void;
  isDropdown?: boolean;
  hideList?: boolean;
  onSeachTermChange?: (s: string) => void;
  CustomPractitionerRenderer?:  React.FC<{ 
    practitioner: any,
    index: number,
  }>;
}

const PractitionersList = ({ defaultTab = Tab.ALL, onPractitionerSelect, onOpenLeadCreate, getFetchPractsAction, isDropdown, hideList, onSeachTermChange, CustomPractitionerRenderer }: Props) => {
  const [practitioners, setPractitioners] = React.useState<any[]>([]);
  const [selectedTab, setSelectedTab] = React.useState<Tab>(defaultTab);
  const [leads, setLeads] = React.useState<any[]>([]);
  // const [totalLeads, setTotalLeads] = React.useState(0);
  const [leadPage, setLeadPage] = React.useState(1);
  const [isLeadsLoading, setIsLeadsLoading] = React.useState(false);
  const [hasMoreLeads, setHasMoreLeads] = React.useState(true);
  const [showToastr, setShowToastr] = React.useState(false);

  const [selectedOption2, setSelectedOption2] = React.useState('January');
  const [pipelineIsClicked, setPipelineIsClicked] = React.useState(false);
  const [funnelData, setFunnelData] = React.useState<any>(null);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(store.getCachedSearchTerm() ? store.getCachedSearchTerm() : '');
  const [searchTerm, setSearchTerm] = React.useState(store.getCachedSearchTerm() ? store.getCachedSearchTerm() : '');

  const [multiSelectOpen, setMultiSelectOpen] = React.useState(false);

  //this is the filter thats used in the filter dropdown
  const [multiSelectValue, setMultiSelectValue] = React.useState<string[]>(store.getPractitionersFilterMultiSelectValue());

  //this is the filter thats used in the filter carousel
  const [selectedFilter, setSelectedFilter] = React.useState(store.getPractitionersCarouselFilter());

  const LIMIT = 50;

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();

  // Track previous search term to avoid unnecessary cache clearing
  const prevSearchTermRef = React.useRef<string | null>(null);
  const prevSelectedFilterRef = React.useRef<string | null>(null);

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay
    if (onSeachTermChange) {
      onSeachTermChange(searchTerm);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Cache search term when it changes
  React.useEffect(() => {
    store.setCachedSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  React.useEffect(() => {
    store.setPractitionersFilterMultiSelectValue(multiSelectValue);
  }, [multiSelectValue]);

  // Hard-coded example data for Pipeline demo (you can remove or replace this)
  const dadosFunel = {
    LEADS: { accounts: 105 },
    PROSPECTS: { accounts: 43 },
    WON: { accounts: 102, color: "#3c7cc0" },
    OPPORTUNITIES: { accounts: 30 },
    funnelPercentages: { leads: 90, prospects: 50, won: 60, opportunities: 10 },
    pipelineData: {
      SALES_READY: { accounts: 105 },
      PREPARE_QUALIFY: { accounts: 43 },
      PRESENT: { accounts: 102 },
      FOLLOW_UP: { accounts: 102 },
      pipeline: 2.2,
      pipelinePercentages: {
        SALES_READY: 22,
        PREPARE_QUALIFY: 22,
        PRESENT: 22,
        FOLLOW_UP: 22,
      },
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectOptions = ['All Time']; //['All Time', 'YTD', 'MTD'];
  const selectOptions2 = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const onCustomerSelected = (customer: any, selectedTab?: BaseballCardTab) => {
    store.setCurrentPractitioner({ ...customer, general_notes: '' });
    onPractitionerSelect(selectedTab);
  };

  const handlePractitionerAdded = () => {
    setShowToastr(true);
  };

  React.useEffect(() => {
    if (selectedTab === "lead") {
      setSelectedTab(Tab.LEAD);
    }
  }, [selectedTab]);

  // Reset list when searchTerm changes
  React.useEffect(() => {
    // Only clear cache if search term actually changed (not on initial mount)
    if (prevSearchTermRef.current !== null && prevSearchTermRef.current !== debouncedSearchTerm) {
      console.log('Clearing cache due to search term change:', prevSearchTermRef.current, '->', debouncedSearchTerm);
      clearCache();
    }
    prevSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm,]);


  React.useEffect(() => {
    if (prevSelectedFilterRef.current !== null && prevSelectedFilterRef.current !== selectedFilter) {
      clearCache();
    }
    prevSelectedFilterRef.current = selectedFilter;
    store.setPractitionersCarouselFilter(selectedFilter);
  }, [selectedFilter]);

  // Clear state when switching tabs to prevent data mixing
  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (selectedTab != Tab.ALL) {
      // Clear practitioners state when switching to LEAD tab
      setPractitioners([]);
      setPage(1);
      setHasMore(true);
    // eslint-disable-next-line eqeqeq
    } if (selectedTab != Tab.LEAD) {
      // Clear leads state when switching to other tabs
      setLeads([]);
      setLeadPage(1);
      setHasMoreLeads(true);
    }

    // Restore total count when switching back to ALL tab
    if (selectedTab === Tab.ALL) {
      const cachedTotal = store.getCachedTotalCount();
      if (cachedTotal > 0) {
        setTotalCount(cachedTotal);
      }
    }
  }, [selectedTab]);

  const fetchPractitioners = useCallback(async (controller?: AbortController) => {
    // Only block pagination requests during loading, not new searches
    if (selectedTab === Tab.LEAD) {
      if (leadPage > 1 && isLeadsLoading) return;
    } else {
      if (page > 1 && isLoading) return;
    }

    //ugly fix
    let toggle = true;

    try {
      if (selectedTab === Tab.LEAD) {
        // Check cache first for leads
        const cachedLeads = store.impersonatedUser ? store.getLeadsCache(store.impersonatedUser, leadPage) : undefined;
        if (cachedLeads) {
          if (leadPage === 1) {
            setLeads(cachedLeads);
          } else {
            setLeads((prev: any[]) => [...prev, ...cachedLeads]);
          }
          return;
        }

        setIsLeadsLoading(true);
        await postData(
          '/api/get_practitioners_for_chat_lead',
          {
            fmc_email: store.impersonatedUser,
            search: debouncedSearchTerm,
            page: leadPage,
            limit: LIMIT,
          },
          controller?.signal
        ).then((data) => {
          // setTotalLeads(data.total);

          // Cache the results
          if (store.impersonatedUser) {
            store.setLeadsCache(store.impersonatedUser, leadPage, data.practitioners || []);
          }
          console.log("leadPage", leadPage);
          if (leadPage === 1) {
            setLeads(data.practitioners || []);
          } else {
            setLeads((prev: any[]) => [...prev, ...(data.practitioners || [])]);
          }
          setIsLeadsLoading(false);

          if (!data.practitioners || data.practitioners.length < LIMIT) {
            setHasMoreLeads(false);
          }
        });
      }
      else {
        // Check cache first for practitioners
        const cachedPractitioners = store.impersonatedUser ? store.getPractitionersCache(store.impersonatedUser, page) : undefined;
        console.log('Checking cache for page:', page, 'cached:', !!cachedPractitioners, 'cache size:', Object.keys(store.practitionersCache).length);
        if (cachedPractitioners) {
          console.log('Using cached data for page:', page);
          if (page === 1) {
            setPractitioners(cachedPractitioners);
            // Restore total count from cache when using cached data
            const cachedTotal = store.getCachedTotalCount();
            if (cachedTotal > 0) {
              setTotalCount(cachedTotal);
              return;
            }
            // EIS-1076: rows cache was populated by another list instance
            // (e.g. the route ADD panel) without the shared total count.
            // Returning here left the header stuck at "0 Accounts" with rows
            // rendered and no fetch ever firing. Fall through so the request
            // below (getTotalCount=true, since the cache is 0) re-derives it.
          } else {
            console.log(cachedPractitioners, "cachedPractitioners");
            setPractitioners((prev: any[]) => [...prev, ...cachedPractitioners]);
            return;
          }
        }

        console.log('Cache miss for page:', page, 'making API call');
        setIsLoading(true);
        var getTotalCount = false;
        // eslint-disable-next-line eqeqeq
        if (store.getCachedTotalCount() == 0) {
          getTotalCount = true;
        }
        await postData(
          '/api/get_practitioners_for_chat',
          {
            fmc_email: store.impersonatedUser,
            search: debouncedSearchTerm,
            page: page,
            limit: LIMIT,
            carouselFilter: selectedFilter,
            dropdownFilter: multiSelectValue,
            alias: store.impersonatedUserAlias,
            getTotalCount: getTotalCount,
          },
          controller?.signal
        ).then((data) => {
          // eslint-disable-next-line eqeqeq
          if (store.cachedTotalCount == 0) {
            setTotalCount(data.total);
            store.setCachedTotalCount(data.total);
          } else {
            // EIS-1076: another list instance sharing the store filled the
            // total while this request was in flight (so data.total may be 0
            // here if this request was sent with getTotalCount=false). Sync
            // the header from the shared cache instead of skipping entirely,
            // otherwise this instance's header can stay at its initial 0.
            setTotalCount(store.getCachedTotalCount());
          }
          // Cache the results
          console.log('Caching data for page:', page, 'data length:', data.practitioners?.length);
          if (store.impersonatedUser) {
            store.setPractitionersCache(store.impersonatedUser, page, data.practitioners || []);
          }

          if (page === 1) {
            setPractitioners(data.practitioners || []);
          } else {
            setPractitioners((prev: any[]) => [...prev, ...(data.practitioners || [])]);
          }

          if (!data.practitioners || data.practitioners.length < LIMIT) {
            setHasMore(false);
          }
        });
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching practitioners:', error);
      } else {
        toggle = false;
      }
    } finally {
      // eslint-disable-next-line eqeqeq
      if (selectedTab != Tab.LEAD && toggle) {
        setIsLoading(false);
      }
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, leadPage, debouncedSearchTerm, selectedTab, selectedFilter, multiSelectOpen]);
  const fetchPractitionersRef = useRef<any>();
  fetchPractitionersRef.current = fetchPractitioners;

  // Fetch practitioners when page or searchTerm changes
  React.useEffect(() => {
    const controller = new AbortController();
    fetchPractitioners(controller);
    return () => controller.abort();
  }, [fetchPractitioners]);

  const clearCache = () => {
    setLeadPage(1);
    setHasMoreLeads(true);
    setPractitioners([]);
    setPage(1);
    setLeads([]);
    setHasMore(true);
    store.clearAllCache();
  };

  useEffect(() => {
    // only set the fetch function for leads
    if (selectedTab === Tab.LEAD) {
      if (getFetchPractsAction) {
        getFetchPractsAction(() => {
          if (leadPage === 1) {
            fetchPractitioners();
          } else {
            clearCache();
          }
        });
      }
    }
    // only set the fetch function for practs
    if (selectedTab === Tab.ALL) {
      if (getFetchPractsAction) {
        getFetchPractsAction(() => {
          if (page === 1) {
            fetchPractitioners();
          } else {
            clearCache();
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, leadPage, page]);

  // Intersection observer to load next page when near bottom
  const observer = React.useRef<IntersectionObserver | null>(null);
  const lastPractitionerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (selectedTab === Tab.ALL && entries[0].isIntersecting && hasMore) {
        setPage((prev) => prev + 1);
      }
      if (selectedTab === Tab.LEAD && entries[0].isIntersecting && hasMoreLeads) {
        setLeadPage((prev) => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, hasMoreLeads, selectedTab]);

  // Fetch funnel data
  React.useEffect(() => {
    const fetchFunnelData = async () => {
      try {
        const data = await postData(
          '/api/e/winback_funnel_data',
          { fmc_email: store.impersonatedUser }
        );
        setFunnelData(data);
      } catch (_err) {
        // handle/log error if needed
      }
    };
    fetchFunnelData();


    //}, [postData]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Build list of filtered practitioners
  const getFilteredPractitioners = () => {
    let filtered = practitioners.slice();

    // 1) Tab filtering
    if (selectedTab === Tab.RECENT) {
      filtered = filtered.filter(p => p.last_contact);
    }
    // if (selectedTab === Tab.LEAD) {
    //   filtered = filtered.filter(
    //     p =>
    //       p.status === "LEAD-INCOMPLETE" ||
    //       p.status === "LEAD-READY2SUBMIT"
    //   );
    // }


    return filtered;
  };

  // EIS-1070: tab-specific PINNED chrome — the search bar, filter chips and legend.
  // Only the ALL and LEAD tabs render this chrome (exactly the tabs that rendered it
  // before the header/body split); it lives in the pinned header and must NOT scroll
  // with the account rows.
  const renderPinnedTabHeader = (): React.ReactNode => {
    if (selectedTab === Tab.FUNNEL || selectedTab === Tab.FOLLOW_UPS || selectedTab === Tab.RECENT) {
      return null;
    }
    return (
      <>
        {/* === SEARCH BAR (only show for ALL, LEAD) === */}
        <div className={styles.searchWrapper}
          style={isDropdown ? ({
            backgroundColor: "white",
            margin: "0",
            width: "calc(100% + 20px)",
            transform: "translateX(-10px)",
            paddingLeft: "10px",
            paddingRight: "10px",
            boxSizing: "border-box"
          }) : {}}>
          <MagnifyingGlassIcon width={20} height={20} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <div style={{ zIndex: 1000, cursor: 'pointer', position: 'relative', marginRight: "10px" }}
            data-filter-container
            onClick={() => {
              if (multiSelectOpen) {
                // If dropdown is open, close it and trigger onClose logic
                setMultiSelectOpen(false);
                clearCache();
              } else {
                // If dropdown is closed, open it
                setMultiSelectOpen(true);
              }
            }}
          >
            <FadersIcon
              width={25}
              height={25}
              style={{ color: multiSelectValue.length < 6 ? "#4F7D9C" : "#8B8B8B" }}
            />


          </div>

          {(searchTerm.trim() !== "") && (selectedTab !== Tab.LEAD) && (
            <button
              className={styles.clearButton}
              onClick={() => setSearchTerm("")}
            >
              X
            </button>
          )}
          {selectedTab === Tab.LEAD && (
            <button
              className={styles.addButton}
              onClick={onOpenLeadCreate}
            >
              Add
            </button>
          )}
          <FilterDropdown
            isOpen={multiSelectOpen}
            selectedValues={multiSelectValue}
            onValueChange={setMultiSelectValue}
            options={["Name", "Address", "City", "State", "Specialty", "Email", "Zipcode", "Tags"]}
            onClose={() => {
              setMultiSelectOpen(false);
              clearCache();
            }}
          />
        </div>
        <p className={styles.infoText}>
          {selectedTab === Tab.ALL && !isDropdown ? (
            <FilterCarousel
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
            />
          ) : (
            <></>
          )
          }
          {!isDropdown && (
            <>
              <div className={styles.filterText}>
                <CheckIcon className={styles.checkIconSmall} />
                Has Lat & Long: Verified Check-In Possible
              </div>
              <div className={styles.filterText}>
                <CurrencyCircleDollarIcon className={styles.dollarIconSmall} />
                Account has sales (current or prior year)
              </div>
              <div className={styles.filterText}>
                <NotesIcon className={styles.checkinIconSmall} />
                Has check-in notes
              </div>
            </>
          )}
        </p>
      </>
    );
  };

  // RENDERING LOGIC FOR TABS — the SCROLLING body (account rows / active tab content only)
  const renderTabBody = (): React.ReactNode => {
    // ------- 1) FUNNEL TAB -------
    if (selectedTab === Tab.FUNNEL) {
      // V2 (earlyRelease): Show new industry-standard 6-stage sales funnel
      // TODO: Re-enable once V2 funnel issues are fixed
      // if (store.user?.earlyRelease) {
      //   return <SalesFunnel />;
      // }

      // V1: Show old funnel with pipeline flow
      // If pipeline not clicked yet, show funnel
      if (!pipelineIsClicked) {
        return (
          <>

            <div className={styles.opportunityText}>
              {/*
              Your total opportunity is $2.2M across{" "}
              <strong>{practitioners.length} active accounts</strong>
              */}
            </div>

            {/*
            <div className={styles.funnelHeader}>
              <div className={styles.funnelHeaderLeft}>Recent account as of</div>
              <select
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                className={styles.periodSelect}
              >
                {selectOptions.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
            </div>
            */}

            {/*
              WARNING: If funnelData is null or data.payload is undefined,
              you might crash here. Always check for funnelData before accessing .payload
            */}
            {funnelData?.payload && (
              <Funnel
                setPipelineIsClicked={setPipelineIsClicked}
                funnelData={funnelData.payload}
              />
            )}
          </>
        );
      }

      // If pipeline arrived at by clicking in Funnel chart
      return (
        <>
          <div className={styles.salesPipeline}>
            <div className={styles.expandIcon}>
              <ExpandIcon
                onClick={() => setPipelineIsClicked(false)}
                width={20}
                height={20}
                style={{ color: "#4F7D9C" }}
              />
            </div>
            <div style={{ paddingLeft: "15px", marginBottom: "15px" }}>
              <div className={styles.salesPipelineText}>Sales Pipeline</div>
              <div className={styles.salesPipelineSubText}>
                Your total opportunity is $2.2M across{" "}
                <strong>{practitioners.length} active accounts</strong>
              </div>
            </div>
          </div>

          <div className={styles.funnelHeader}>
            <div className={styles.funnelHeaderLeft}>By Expected Close</div>
            <select
              value={selectedOption2}
              onChange={(e) => setSelectedOption2(e.target.value)}
              className={styles.periodSelect}
            >
              {selectOptions2.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </div>

          <Pipeline pipelineData={dadosFunel.pipelineData} />
        </>
      );
    }

    if (selectedTab === Tab.FOLLOW_UPS) {
      return (
        <FollowUpsTab
          onClickPractitioner={(practitioner: any) => onCustomerSelected(practitioner)}
          onClickPractitionerCheckin={(practitioner: any) => onCustomerSelected(practitioner, PractitionerBaseballCardTab.CHECK_IN)}
          onPractitionerAddedToRoute={handlePractitionerAdded}
        />
      );
    }

    if (selectedTab === Tab.RECENT) {
      return (
        <LatestPractitionersUpdatedList
          onCustomerSelected={onCustomerSelected} />
      )
    }

    // ------- 2) ALL / RECENT / LEAD TABS -------
    // Build the filtered list (will be the same for ALL, RECENT, LEAD)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const filteredPractitioners = getFilteredPractitioners();

    return (
      <>
        {!hideList && (
          <>
            {(selectedTab === Tab.LEAD ? leads : practitioners)?.length === 0
              && (selectedTab === Tab.LEAD ? !isLeadsLoading : !isLoading)
              && (selectedTab === Tab.LEAD && store.impersonatedUser && store.getLeadsCache(store.impersonatedUser, 1)) &&
              (
                <div>No results</div>
              )}
            {(selectedTab === Tab.LEAD ? leads : practitioners)?.length > 0 && (
              /*filteredPractitioners.map((customer: any, index: number) => (
                <div
                  className={styles.customer}
                  key={customer.id}
                  onClick={() => onCustomerClick(customer)}
                >
                  <p className={styles.practitionerName}>
                    {`${index + 1}. ${customer.primary_name}`}
                  </p>
                  {customer.status === "LEAD-INCOMPLETE" && (
                    <div className={styles.leadFlag}>
                      <span>LEAD</span>
                    </div>
                  )}
                  {customer.status === "LEAD-READY2SUBMIT" && (
                    <div className={styles.leadFlagComplete}>
                      <span>LEAD</span>
                    </div>
                  )}
                  <p className={styles.category}>{customer.specialty}</p>
                </div>
              )*/
              (selectedTab === Tab.LEAD ? leads : practitioners)?.map((customer: any, index: number) => {
                // 1) Determine the data‐source pill text:
                const isGeoValidted = customer?.addresses?.some(
                  (a: any) => a?.latitude && a?.longitude
                );
                const hasMtdYtd = (customer?.ytd_sales > 0 || customer?.mtd_sales > 0 || customer?.p_ytd_sales > 0 || customer?.rolling_12m_sales > 0) && customer.data_source !== "PRACTITIONER_ACCOUNT_VIEW_NM";
                let dataSourcePillText: string | null = null;
                switch (customer.data_source) {
                  case "PRACTITIONER_ACCOUNT_VIEW":
                    dataSourcePillText = null; // no pill for this case
                    break;
                  case "PRACTITIONER_ACCOUNT_VIEW_NM":
                    dataSourcePillText = "Badger";
                    break;
                  case "PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW":
                    switch (customer.name_dfh_channel) {
                      case "":
                        dataSourcePillText = `Dist`;
                        break;
                      case "DFH":
                        dataSourcePillText = `Dist: ${customer.name_dfh_channel}`;
                        break;
                      default:
                        dataSourcePillText = `${customer.name_dfh_channel}`;
                        break;
                    }
                    break;
                  default:
                    dataSourcePillText = null;
                }

                const isNearBottom = index > (selectedTab === Tab.LEAD ? leads.length : practitioners.length) - 50;
                if (CustomPractitionerRenderer) {
                  return (
                    <div style={{ margin: 0, padding: 0, boxSizing: "border-box"}}
                      ref={isNearBottom ? lastPractitionerRef : undefined}
                      key={customer.id}
                      onClick={() => onCustomerSelected(customer)}>
                      <CustomPractitionerRenderer
                        index={index}
                        practitioner={customer} />
                    </div>
                  );
                }
                return (
                  <div
                    ref={isNearBottom ? lastPractitionerRef : undefined}
                    className={styles.customer}
                    key={customer.id}
                    onClick={() => onCustomerSelected(customer)}
                  >
                    <div className={styles.leftWrapper}>
                      {/*<div className={styles.nameRow}>*/}
                      <div className={styles.counterColumn}>
                        <span className={styles.practitionerCounter}>
                          {`${index + 1}.`}
                        </span>
                      </div>
                      <div className={styles.leftColumn}>
                        <span className={styles.practitionerName}>
                          {`${customer.primary_name}`}
                        </span>
                        <span className={styles.category}>{customer.specialty}</span>
                      </div>
                    </div>
                    <div className={styles.rightColumn}>
                      {hasMtdYtd && (
                        <CurrencyCircleDollarIcon className={styles.dollarIcon} />
                      )}
                      {/* EIS-1067: check-in indicator — rendered when the list
                          endpoint reports at least one check-in (native +
                          imported NetSuite + imported Badger).
                          EIS-1085 Part 2: also render when the account notes
                          blob has non-whitespace text (check-ins remain the
                          backend has_checkins semantics; notes is frontend-only). */}
                      {(customer.has_checkins || !!customer.notes?.trim()) && (
                        <span
                          className={styles.checkinIndicator}
                          title="Has check-ins"
                          data-testid="checkin-indicator"
                        >
                          <NotesIcon className={styles.checkinIcon} />
                        </span>
                      )}
                      {isGeoValidted && (
                        <CheckIcon className={styles.checkIcon} />
                      )}
                      {/*
                  2) If this practitioner has a "data source pill," render it here.
                     It will inherit CSS from .dataSourcePill (red background, white text, rounded).
                */}
                      {
                        customer.tags?.mine?.filter((tag: any) => tag.selected === true).length > 0 ?
                          <div style={{ backgroundColor: "#FF9D00" }} className={styles.dataSourcePill}>
                            {(() => {
                              const tagName = customer.tags?.mine?.filter((tag: any) => tag.selected === true)[0]?.name;
                              return tagName?.length > 10 ? `${tagName.substring(0, 10)}...` : tagName;
                            })()}
                          </div>
                          : null
                      }
                      {dataSourcePillText && (
                        <div className={styles.dataSourcePill}>
                          {dataSourcePillText}
                        </div>
                      )}
                      {customer.status === "LEAD-INCOMPLETE" && (
                        <div className={styles.leadFlag}>
                          <span>LEAD</span>
                        </div>
                      )}
                      {customer.status === "LEAD-READY2SUBMIT" && (
                        <div className={styles.leadFlagComplete}>
                          <span>LEAD</span>
                        </div>
                      )}

                      {/*<p className={styles.category}>{customer.specialty}</p>*/}
                    </div>
                  </div>
                );
              })
            )}
            {(selectedTab === Tab.LEAD ? isLeadsLoading : isLoading) && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                <Loader size="lg" />
              </div>
            )}
          </>
        )}
        {/* === CUSTOMER LIST === */}

      </>
    );
  };

  // =============== FINAL RENDER ===============
  return (
    <>
      <div className={isDropdown ? styles.wrapperLegacy : styles.wrapper}
        style={isDropdown ? { background: "rgba(255, 255, 255, 0)", minHeight: "270px"} : { background: "auto" }}>
        {/* EIS-1070: pinned chrome — title, count, tabs, search, chips, legend. In
            isDropdown (map search) mode both split divs are display:contents, i.e. the
            legacy single-column layout is preserved exactly. */}
        <div
          className={isDropdown ? styles.legacyGroup : styles.pinnedHeader}
          data-testid="practitioner-list-header"
        >
        {!isDropdown && (
          <>
            <h2 className={styles.header}>Your Accounts</h2>
            <p className={styles.customerCounter}>
              {totalCount} Accounts
            </p>

            <div className={styles.horizontalScrollWrapper}>
              {/* ALL TAB */}
              <div
                className={`
              ${styles.horizontalScrollItemWrapper}
              ${styles.tab}
              ${selectedTab === Tab.ALL ? styles.selectedTab : ""}
            `}
                onClick={() => {
                  setSelectedTab(Tab.ALL);
                  setPipelineIsClicked(false);
                }}
              >
                <UserIcon width={24} height={24} />
                <span>All</span>
              </div>

              {/* RECENT TAB */}
              <div
                className={`${styles.horizontalScrollItemWrapper} ${styles.tab} ${selectedTab === Tab.RECENT ? styles.selectedTab : ''}`}
                onClick={() => setSelectedTab(Tab.RECENT)}
              >
                <ClockCounterClockwiseIcon width={24} height={24} />
                <span>Recent</span>
              </div>


              {/* LEAD TAB */}
              <div
                className={`
              ${styles.horizontalScrollItemWrapper}
              ${styles.tab}
              ${selectedTab === Tab.LEAD ? styles.selectedTab : ""}
            `}
                onClick={() => {
                  setSelectedTab(Tab.LEAD);
                  setPipelineIsClicked(false);
                }}
              >
                <LeadIcon width={24} height={24} />
                <span>Leads</span>
              </div>

              {/* FOLLOW UPS TAB */}
              <div
                className={`
              ${styles.horizontalScrollItemWrapper}
              ${styles.tab}
              ${selectedTab === Tab.FOLLOW_UPS ? styles.selectedTab : ""}
            `}
                onClick={() => {
                  setSelectedTab(Tab.FOLLOW_UPS);
                  setPipelineIsClicked(false);
                }}
              >
                {selectedTab === Tab.FOLLOW_UPS ? (
                  <BlueAlarmIcon width={24} height={24} />
                ) : (
                  <AlarmIcon width={24} height={24} />
                )}
                <span>Follow-Ups</span>
              </div>

              {/* FUNNEL TAB */}
              <div
                className={`
                ${styles.horizontalScrollItemWrapper}
                ${styles.tab}
                ${selectedTab === Tab.FUNNEL ? styles.selectedTab : ""}
              `}
                onClick={() => {
                  setSelectedTab(Tab.FUNNEL);
                  setPipelineIsClicked(false);
                }}
              >
                {selectedTab === Tab.FUNNEL ? (
                  <BlueFunnelIcon width={24} height={24} />
                ) : (
                  <FunnelIcon width={24} height={24} />
                )}
                <span>Funnel</span>
              </div>
            </div>
          </>
        )}
        {renderPinnedTabHeader()}
        </div>

        {/* EIS-1070: only this region scrolls (account rows / active tab content). */}
        <div
          className={isDropdown ? styles.legacyGroup : styles.scrollBody}
          data-testid="practitioner-list-body"
        >
          {renderTabBody()}
        </div>
      </div>

      <Toastr
        message="Practitioner added to route"
        isVisible={showToastr}
        onClose={() => setShowToastr(false)}
        backgroundColor="white"
        closeTime={2000}
      />
    </>
  );
};

export default PractitionersList;